/**
 * Clients Service
 *
 * CRUD operations for client management.
 */

import apiClient, {
  APIResponse,
  PaginatedResponse,
  unwrapResponse,
  unwrapPaginatedResponse,
} from './apiClient';
import { ENDPOINTS, PAGINATION } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export type ClientType = 'PARTICULIER' | 'ENTREPRISE' | 'COOPERATIVE' | 'ETAT_ONG';
export type CategorieClient = 'DETAILLANT' | 'GROSSISTE';

export interface Client {
  id: string;
  code: string;
  nom: string;
  raison_sociale?: string;
  type_client: ClientType;
  categorie_client: CategorieClient;
  provenance_id: string;
  provenance_libelle?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  ville?: string;
  ifu?: string;
  titre_exoneration?: string;
  is_active: boolean;
  score_solvabilite?: number;
  notes?: string;
  created_at: string;
  updated_at: string;

  // Computed fields
  is_exonere: boolean;
}

export interface ClientCreate {
  nom: string;
  raison_sociale?: string;
  type_client: ClientType;
  categorie_client?: CategorieClient;
  provenance_id: string;
  email?: string;
  telephone: string;
  adresse?: string;
  ville?: string;
  ifu?: string;
  titre_exoneration?: string;
  notes?: string;
}

export interface ClientUpdate {
  nom?: string;
  raison_sociale?: string;
  type_client?: ClientType;
  categorie_client?: CategorieClient;
  provenance_id?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  ville?: string;
  ifu?: string;
  titre_exoneration?: string;
  is_active?: boolean;
  notes?: string;
}

export interface ClientListParams {
  page?: number;
  page_size?: number;
  search?: string;
  type?: ClientType;
  categorie?: CategorieClient;
  is_active?: boolean;
  provenance_id?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface ClientStatistics {
  total_clients: number;
  active_clients: number;
  inactive_clients: number;
  clients_by_type: Record<ClientType, number>;
  clients_by_provenance: Record<string, number>;
  average_solvency: number;
}

// =============================================================================
// Clients Service
// =============================================================================

export const clientsService = {
  /**
   * List clients with pagination and filters
   */
  async list(params: ClientListParams = {}) {
    const {
      page = PAGINATION.DEFAULT_PAGE,
      page_size = PAGINATION.DEFAULT_PAGE_SIZE,
      ...filters
    } = params;

    const response = await apiClient.get<PaginatedResponse<Client>>(ENDPOINTS.CLIENTS, {
      params: {
        page,
        page_size,
        ...filters,
      },
    });

    return unwrapPaginatedResponse(response);
  },

  /**
   * Get a single client by ID
   */
  async get(id: string): Promise<Client> {
    const response = await apiClient.get<APIResponse<Client>>(
      `${ENDPOINTS.CLIENTS}/${id}`
    );
    return unwrapResponse(response);
  },

  /**
   * Create a new client
   */
  async create(data: ClientCreate): Promise<Client> {
    const response = await apiClient.post<APIResponse<Client>>(
      ENDPOINTS.CLIENTS,
      data
    );
    return unwrapResponse(response);
  },

  /**
   * Update an existing client
   */
  async update(id: string, data: ClientUpdate): Promise<Client> {
    const response = await apiClient.patch<APIResponse<Client>>(
      `${ENDPOINTS.CLIENTS}/${id}`,
      data
    );
    return unwrapResponse(response);
  },

  /**
   * Delete a client (soft delete)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${ENDPOINTS.CLIENTS}/${id}`);
  },

  /**
   * Toggle client active status
   */
  async toggleActive(id: string, isActive: boolean): Promise<Client> {
    return this.update(id, { is_active: isActive });
  },

  /**
   * Get client statistics
   */
  async getStatistics(): Promise<ClientStatistics> {
    const response = await apiClient.get<APIResponse<ClientStatistics>>(
      `${ENDPOINTS.CLIENTS}/statistics`
    );
    return unwrapResponse(response);
  },

  /**
   * Search clients by name/code
   */
  async search(query: string, limit = 10): Promise<Client[]> {
    const { data } = await this.list({
      search: query,
      page_size: limit,
    });
    return data;
  },

  /**
   * List provenances for dropdown
   */
  async listProvenances(): Promise<{ id: string; nom: string; code: string }[]> {
    const response = await apiClient.get<PaginatedResponse<any>>(
      ENDPOINTS.PROVENANCES,
      { params: { page_size: 100, active_only: true } }
    );
    const items = unwrapPaginatedResponse(response).data;
    return items.map((p: any) => ({ id: p.id, nom: p.libelle || p.nom || '', code: p.libelle || p.code || '' }));
  },
};

export default clientsService;
